<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Tarifario</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">

          <!-- Toggle de dirección -->
          <div class="d-flex mb-2 mb-md-0">
            <button
              type="button"
              :class="['button', routeType === 'district_to_airport' ? 'button-primary' : 'button-light']"
              @click="setRouteType('district_to_airport')"
            >
              Distrito → Aeropuerto
            </button>
            <button
              type="button"
              :class="['button ml-2', routeType === 'airport_to_district' ? 'button-primary' : 'button-light']"
              @click="setRouteType('airport_to_district')"
            >
              Aeropuerto → Distrito
            </button>
          </div>

          <!-- Buscador -->
          <div class="d-flex align-items-center">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover title="Recargar"
                @click="loadData()">
                <i class="ri-loop-right-line"></i>
              </button>
            </div>
            <SearchBar placeholder="Buscar distrito..." @search="searchText = $event" @input="searchText = $event" class="ml-md-2" />
          </div>
        </div>
      </div>

      <div class="mb-0" style="overflow-x: auto;">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th rowspan="2" class="th-distrito align-middle">Distrito</th>
              <th colspan="4" class="text-center th-base-header">Costo Base</th>
              <th colspan="4" class="text-center th-publico-header">Público</th>
              <th colspan="4" class="text-center th-qore-header">Qore</th>
              <th colspan="4" class="text-center th-ff-header">Cliente FF</th>
            </tr>
            <tr>
              <th v-for="vt in vehicleTypes" :key="`base-${vt}`" class="text-center th-base-sub">{{ vt }}</th>
              <th v-for="vt in vehicleTypes" :key="`pub-${vt}`" class="text-center th-publico-sub">{{ vt }}</th>
              <th v-for="vt in vehicleTypes" :key="`qore-${vt}`" class="text-center th-qore-sub">{{ vt }}</th>
              <th v-for="vt in vehicleTypes" :key="`ff-${vt}`" class="text-center th-ff-sub">{{ vt }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="filteredRows.length === 0">
              <td colspan="17" class="text-center">No se encontraron distritos</td>
            </tr>
            <tr v-for="row in filteredRows" :key="row.id">
              <td class="font-weight-bold">{{ row.name }}</td>
              <td v-for="vt in vehicleTypes" :key="`base-${vt}`" class="text-center td-base">{{ getVal(row, vt, 'base') }}</td>
              <td v-for="vt in vehicleTypes" :key="`pub-${vt}`" class="text-center">{{ getVal(row, vt, 'publico') }}</td>
              <td v-for="vt in vehicleTypes" :key="`qore-${vt}`" class="text-center">{{ getVal(row, vt, 'qore') }}</td>
              <td v-for="vt in vehicleTypes" :key="`ff-${vt}`" class="text-center">{{ getVal(row, vt, 'cliente_ff') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BasePriceService } from '@/core/services'
import { Alert, SearchBar } from 'wize-admin'

const PRIORITY = ['Miraflores', 'Barranco', 'San Isidro', 'Santiago de Surco', 'La Molina']
const VEHICLE_TYPES = ['Sedan', 'SUV', 'VAN', 'Sprinter']

export default {
  components: { SearchBar },
  data() {
    return {
      vehicleTypes: VEHICLE_TYPES,
      routeType: 'district_to_airport',
      searchText: '',
      rows: [],
    }
  },
  computed: {
    districtMap() {
      const map = {}
      this.rows.forEach(r => {
        if (!map[r.district_id]) {
          map[r.district_id] = { id: r.district_id, name: r.district_name, vehicles: {} }
        }
        const vData = { base: r.base_amount }
        ;(r.prices || []).forEach(p => { vData[p.price_type_code] = p.calculated_amount })
        map[r.district_id].vehicles[r.vehicle_type] = vData
      })
      return map
    },
    filteredRows() {
      const q = this.searchText.trim().toLowerCase()

      const hasPrices = (id) => {
        const d = this.districtMap[id]
        return d && Object.values(d.vehicles).some(v => (v.base ?? 0) > 0)
      }

      const priorityIndex = (name) => {
        const i = PRIORITY.indexOf(name)
        return i >= 0 ? i : Infinity
      }

      return Object.values(this.districtMap)
        .filter(d => !q || d.name.toLowerCase().includes(q))
        .sort((a, b) => {
          const pa = priorityIndex(a.name)
          const pb = priorityIndex(b.name)
          if (pa !== Infinity && pb !== Infinity) return pa - pb
          if (pa !== Infinity) return -1
          if (pb !== Infinity) return 1
          const ha = hasPrices(a.id)
          const hb = hasPrices(b.id)
          if (ha && !hb) return -1
          if (!ha && hb) return 1
          return a.name.localeCompare(b.name)
        })
    },
  },
  methods: {
    getVal(row, vehicleType, priceCode) {
      const v = row.vehicles[vehicleType]
      if (!v) return '—'
      const amount = priceCode === 'base' ? v.base : v[priceCode]
      return amount != null ? `S/ ${parseFloat(amount).toFixed(2)}` : '—'
    },
    setRouteType(rt) {
      if (rt === this.routeType) return
      this.routeType = rt
      this.loadData()
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await BasePriceService.getCalculatedPrices(this.routeType)
        this.rows = res.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e?.errors?.message || 'Error al cargar los precios calculados')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async mounted() {
    await this.loadData()
  },
}
</script>

<style lang="stylus" scoped>
.th-distrito
  min-width 140px

/* Encabezados de grupo (primera fila) */
.th-base-header
  background-color #e8f5e9
  color #2e7d32
  border-bottom 2px solid #a5d6a7

.th-publico-header
  background-color #e3f2fd
  color #1565c0
  border-bottom 2px solid #90caf9

.th-qore-header
  background-color #f3e5f5
  color #6a1b9a
  border-bottom 2px solid #ce93d8

.th-ff-header
  background-color #fff3e0
  color #e65100
  border-bottom 2px solid #ffcc80

/* Sub-encabezados de vehículo (segunda fila) */
.th-base-sub
  background-color #f1f8f2
  color #388e3c
  font-weight normal
  min-width 90px

.th-publico-sub
  background-color #f0f7ff
  color #1976d2
  font-weight normal
  min-width 90px

.th-qore-sub
  background-color #faf0fc
  color #7b1fa2
  font-weight normal
  min-width 90px

.th-ff-sub
  background-color #fffaf0
  color #f57c00
  font-weight normal
  min-width 90px

/* Celdas de Costo Base ligeramente resaltadas */
.td-base
  color #2e7d32
  font-weight 500
</style>
